import React, { FormEventHandler, useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Link, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowsProp } from '@mui/x-data-grid';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';

import { getLuxLoyaltyActivityLog, getLuxLoyaltyCustomerPendingPoints } from '~/services/LuxLoyalty';
import { Dayjs, formatDateShortDD, getDayJs, isBetween } from '~/services/TimeService';

import { sortBy } from '~/utils/arrayUtils';
import { formatDecimal } from '~/utils/numberFormatters';

import ErrorDisplay from '../Common/ErrorDisplay';

const columns: Array<GridColDef> = [
  {
    field: 'activityDate',
    flex: 1,
    headerName: 'Activity Date',
    sortable: true,
    display: 'flex',
    valueGetter: (value) => formatDateShortDD(value),
  },
  {
    field: 'activityType',
    flex: 1,
    headerName: 'Activity Type',
    sortable: true,
    display: 'flex',
  },
  {
    field: 'activityDescription',
    flex: 1,
    headerName: 'Activity Description',
    sortable: false,
    display: 'flex',
  },
  {
    field: 'statusCredits',
    flex: 1,
    headerName: 'Status Credits',
    sortable: false,
    display: 'flex',
    valueFormatter: (value) => (value ? formatDecimal(value) : undefined),
  },
  {
    field: 'bonusStatusCredits',
    flex: 1,
    headerName: 'Bonus Status Credits',
    sortable: false,
    display: 'flex',
    valueFormatter: (value) => (value ? formatDecimal(value) : undefined),
  },
  {
    field: 'points',
    type: 'number',
    flex: 1,
    headerName: 'Points',
    sortable: false,
  },
  {
    field: 'bonusPoints',
    type: 'number',
    flex: 1,
    headerName: 'Bonus Points',
    sortable: false,
  },
  {
    field: 'numberOfNights',
    type: 'number',
    flex: 1,
    headerName: 'Number of Nights',
    sortable: false,
  },
  {
    field: 'currencyAmount',
    type: 'number',
    flex: 1,
    headerName: 'Currency Amount',
    sortable: false,
  },
  {
    field: 'currency',
    type: 'number',
    flex: 1,
    headerName: 'Currency',
    sortable: false,
    valueGetter: (value, row) => (row.currencyAmount ? value : undefined),
  },
  {
    field: 'orderId',
    flex: 1,
    headerName: 'Order ID',
    sortable: false,
    display: 'flex',
    renderCell: (params: GridRenderCellParams) => (
      <Link target="_blank" href={`/purchases/${params.row.orderId}`}>
        {params.row.orderId}
      </Link>
    ),
  },
  {
    field: 'itemId',
    flex: 1,
    headerName: 'Item ID',
    sortable: false,
  },
];

interface Props {
  account: App.LuxLoyaltyAccount;
}

export default function LuxLoyaltyActivityLog({ account }: Props) {
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([getDayJs().subtract(2, 'week'), getDayJs()]);
  const [fromDay, toDay] = dateRange;
  const fromDate = fromDay?.format('YYYY-MM-DD');
  const toDate = toDay?.format('YYYY-MM-DD');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activityLog, setActivityLog] = useState<App.LuxLoyaltyActivityLog>([]);
  const [pendingPoints, setPendingPoints] = useState<Array<App.LuxLoyaltyPendingPoint>>([]);
  const filteredPendingPoints = useMemo<Array<App.LuxLoyaltyActivity>>(
    () =>
      pendingPoints
        .filter((pendingPoint) => {
          return isBetween(fromDate, toDate, pendingPoint.createdAt);
        })
        .map(
          (pendingPoint): App.LuxLoyaltyActivity => ({
            ...pendingPoint,
            activityDate: pendingPoint.createdAt,
            activityType: 'Purchase',
            activityDescription: 'Pending Points',
            // pendingPoint.points is base + bonus; the grid reports them in
            // separate columns, so keep the Points column to base points only
            points: pendingPoint.basePoints,
          }),
        ),
    [pendingPoints, fromDate, toDate],
  );

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (event) => {
      event.preventDefault();
      try {
        setError(null);
        setIsLoading(true);
        const activityLog = await getLuxLoyaltyActivityLog(account.customerId, fromDate, toDate);
        setActivityLog(activityLog);
      } catch (error) {
        setError(error?.message ?? String(error));
      } finally {
        setIsLoading(false);
      }
    },
    [account.customerId, fromDate, toDate],
  );

  const fetchPendingPoints = useCallback(async () => {
    try {
      const pendingPoints = await getLuxLoyaltyCustomerPendingPoints(account.customerId);
      setPendingPoints(pendingPoints);
    } catch (error) {
      setError(error?.message ?? String(error));
    } finally {
      setIsLoading(false);
    }
  }, [account.customerId]);

  useEffect(() => {
    fetchPendingPoints();
  }, [fetchPendingPoints]);

  const rows = useMemo<GridRowsProp<App.LuxLoyaltyActivity>>(() => {
    return sortBy([...activityLog, ...filteredPendingPoints], (row) => row.activityDate, 'asc');
  }, [activityLog, filteredPendingPoints]);

  return (
    <Stack direction="column" gap={4} flex={1}>
      <form onSubmit={handleSubmit}>
        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
          <DateRangePicker value={dateRange} onChange={setDateRange} localeText={{ start: 'From', end: 'To' }} />
          <Button type="submit" variant="contained" disabled={!fromDate || !toDate}>
            Submit
          </Button>
        </Stack>
      </form>
      {error && <ErrorDisplay message={error} />}
      {!error && (
        <DataGrid density="standard" rows={rows} columns={columns} disableRowSelectionOnClick loading={isLoading} />
      )}
    </Stack>
  );
}
